import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { describeSelection, sectionLabel } from '../../utils/venueSections';

interface PitchSectionSelectorProps {
  /** A pálya részeinek száma (Venue.sections, 1–4) */
  sectionCount: number;
  /** Kijelölt részek (mind kijelölve = egész pálya) */
  selected: number[];
  onChange: (sections: number[]) => void;
  /** A választott idősávban más által foglalt részek — piros jelölést kapnak */
  occupiedSections?: number[];
  /** Van teljes pályát foglaló ütközés az idősávban */
  fullyOccupied?: boolean;
  /** A részek nevei (Venue.sectionNames) - név híján "N. rész" */
  sectionNames?: string[] | null;
}

/**
 * Vizuális pályarész-választó: a pálya rajzán a részekre kattintva jelölhető ki
 * negyed / fél / egész pálya. 2 rész: két félpálya egymás mellett; 4 rész: 2x2 rács.
 */
const PitchSectionSelector: React.FC<PitchSectionSelectorProps> = ({
  sectionCount,
  selected,
  onChange,
  occupiedSections = [],
  fullyOccupied = false,
  sectionNames,
}) => {
  if (sectionCount <= 1) return null;

  const sections = Array.from({ length: sectionCount }, (_, i) => i + 1);
  const isSelected = (s: number) => selected.includes(s);
  const isOccupied = (s: number) => fullyOccupied || occupiedSections.includes(s);
  const allSelected = selected.length >= sectionCount;

  const toggle = (s: number) => {
    // Teljes pályánál (mind kijelölve) a kattintás CSAK azt a részt jelöli ki —
    // különben a felhasználó szándéka ellenkezőjére fordulna (kivenné a részt)
    if (allSelected) {
      onChange([s]);
      return;
    }
    if (isSelected(s)) {
      // az utolsó kijelölt rész levétele = vissza a teljes pályához
      if (selected.length <= 1) {
        onChange(sections);
        return;
      }
      onChange(selected.filter((x) => x !== s));
    } else {
      onChange([...selected, s].sort((a, b) => a - b));
    }
  };

  const gridClass =
    sectionCount === 2 ? 'grid-cols-2' : sectionCount === 3 ? 'grid-cols-3' : 'grid-cols-2';

  return (
    <div className="mt-2">
      <div className="relative overflow-hidden rounded-lg ring-1 ring-gray-300">
        {/* Pálya-dekoráció: középkör és felezővonal */}
        <div className="pointer-events-none absolute inset-0 z-10">
          <div className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white/50" />
          <div
            className={`absolute bg-white/50 ${
              sectionCount === 2 || sectionCount === 3
                ? 'inset-y-0 left-1/2 w-0.5 -translate-x-1/2'
                : 'left-0 right-0 top-1/2 h-0.5 -translate-y-1/2'
            }`}
          />
        </div>

        <div className={`grid ${gridClass} gap-0.5 bg-white`}>
          {sections.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => toggle(s)}
              aria-pressed={isSelected(s)}
              className={`relative flex h-16 items-center justify-center text-sm font-semibold transition-colors sm:h-20 ${
                isSelected(s)
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-green-900/40 text-white/80 hover:bg-green-900/30'
              }`}
            >
              <span>{sectionLabel(s, sectionNames)}</span>
              {isOccupied(s) && (
                <span
                  className="absolute right-1 top-1 z-20 flex items-center gap-0.5 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-medium text-white"
                  title="Ebben az idősávban ez a rész már foglalt"
                >
                  <ExclamationTriangleIcon className="h-3 w-3" />
                  foglalt
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-1.5 flex items-center justify-between gap-2">
        <p className="text-xs text-gray-600">{describeSelection(selected, sectionCount, sectionNames)}</p>
        {!allSelected && (
          <button
            type="button"
            onClick={() => onChange(sections)}
            className="text-xs font-medium text-primary-600 hover:text-primary-700"
          >
            Egész pálya kijelölése
          </button>
        )}
      </div>
    </div>
  );
};

export default PitchSectionSelector;
